import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { todayISO } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/admin/DateInput";
import { Field, MoneyInput, BigButton } from "@/components/admin/common";
import { AccountSelect, useAccounts } from "@/components/finance/shared";

export default function PaymentDialog({ open, onClose, booking, direction, onSaved }) {
  const [accounts] = useAccounts();
  const [f, setF] = useState({ amount: "", date: todayISO(), cash_account: "", note: "" });
  const [busy, setBusy] = useState(false);
  const guest = direction === "guest";

  const due = booking ? (guest ? booking.price - booking.guest_paid : booking.vendor_cost - booking.vendor_paid) : 0;
  useEffect(() => {
    if (!open || !booking) return;
    setF({ amount: due > 0 ? due : "", date: todayISO(), cash_account: "1-1200", note: "" });
    api.get("/admin/finance/last-used").then((r) => {
      if (r.data.cash_account) setF((p) => ({ ...p, cash_account: r.data.cash_account }));
    });
  }, [open, booking, guest]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    if (!f.amount) return toast.error("Enter an amount");
    setBusy(true);
    try {
      const r = await api.post(`/admin/bookings/${booking.id}/payments`, { ...f, direction });
      toast.success("Payment recorded — finance entry created");
      onSaved(r.data);
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="font-admin" data-testid="payment-dialog">
        <DialogHeader><DialogTitle>{guest ? "Record guest payment" : "Record boat vendor payment"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Amount"><MoneyInput value={f.amount} onChange={(v) => setF({ ...f, amount: v })} testid="payment-amount-input" className="text-lg font-bold" />
            {Number(f.amount) > Math.max(due, 0) && <p className="text-xs font-semibold text-amber-700 mt-1" data-testid="payment-overpay-warning">This is more than the remaining balance.</p>}
          </Field>
          <Field label={guest ? "Received into (bank / cash)" : "Paid from (bank / cash)"}>
            <AccountSelect accounts={accounts.filter((a) => a.is_cash)} value={f.cash_account} onChange={(v) => setF({ ...f, cash_account: v })} grouped={false} testid="payment-account-select" />
          </Field>
          <Field label="Date"><DateInput value={f.date} onChange={(v) => v && setF((p) => ({ ...p, date: v }))} testid="payment-date-input" /></Field>
          <Field label="Note (optional)"><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="h-12" placeholder={guest ? "e.g. deposit via transfer" : "e.g. 50% boat deposit"} data-testid="payment-note-input" /></Field>
          <BigButton type="submit" disabled={busy} className="w-full" data-testid="payment-save-btn">{busy ? "Saving…" : "Save payment"}</BigButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}
